// src/lib/pharmacyFirst/pathways/aht/aht-wt-foundayo-reinitiation.pathway.ts
//
// AHT Weight Management — Orforglipron (Foundayo®) Re-initiation Supply Pathway
// Source: AHT Foundayo PGD v2.1 (Re-initiation criteria (returning patients); Dosage & Frequency > Interruption and re-initiation).
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// The shared assessment has already confirmed: original BMI documented, at least 5% response, within 12 months of the last dose,
// documented weight regain, and the 22.5 kg/m² floor. This pathway records the evidence and sets the restart dose from the gap.
// Gap of 30 days or less: resume the last tolerated dose. Gap over 30 days: restart at 0.8 mg. Over 12 months: new initiation.

import type { Pathway } from "../../types/pathway.types";
import { buildFoundayoSupplyDownstream, foundayoRedirectOutcome } from "./_shared/foundayo-supply-downstream";
import { FDY_REINIT_MAX_DAYS, FDY_RESUME_MAX_GAP_DAYS, daysSinceIsoDate } from "./_shared/foundayo-route-helpers";

const Q_ORIGINAL_BMI = "fdy-reinit-q-original-bmi";
const Q_EVIDENCE_TEXT = "fdy-reinit-q-evidence-of-response";
const Q_RATIONALE = "fdy-reinit-q-rationale";
const Q_LAST_DOSE_DATE = "fdy-reinit-q-last-dose-date";
const R_GAP = "fdy-reinit-gap-route";
const Q_BELOW_ENTRY = "fdy-reinit-q-below-entry-bmi";
const Q_OFF_LABEL = "fdy-reinit-q-off-label-consent";
const Q_CAP = "fdy-reinit-q-dose-cap";
const Q_RESUME_DOSE = "fdy-reinit-q-resume-dose";
const Q_RESUME_DOSE_CAPPED = "fdy-reinit-q-resume-dose-capped";
const R_RESTART_DOSE = "fdy-reinit-restart-dose-route";

const OFF_LABEL_ITEMS = ["fdy_ol_explained", "fdy_ol_reason", "fdy_ol_consent"] as const;

const OUT_OVER_12M = "FDY_OUT_REINIT_OVER_12_MONTHS";
const OUT_OFF_LABEL_NOT_RECORDED = "FDY_OUT_OFF_LABEL_NOT_RECORDED";

const STEP_OPTIONS = [
  { id: "fdy-0_8mg", label: "0.8 mg tablets" },
  { id: "fdy-2_5mg", label: "2.5 mg tablets" },
  { id: "fdy-5_5mg", label: "5.5 mg tablets" },
  { id: "fdy-9mg", label: "9 mg tablets" },
  { id: "fdy-14_5mg", label: "14.5 mg tablets" },
  { id: "fdy-17_2mg", label: "17.2 mg tablets" },
];

const downstream = buildFoundayoSupplyDownstream({
  idPrefix: "fdy-reinit",
  attestHeading: "Confirm re-initiation supply of Foundayo® tablets",
  attestLead: [
    "Supplied one intact 30-tablet pack of the single tablet strength selected above, never a split 90-tablet pack and never more than one month",
    "Confirmed the tablet imprint (G1 to G6) matches the strength selected, and that the stock came from an MHRA registered wholesaler with the supplier recorded",
    "Recorded the original BMI, the current BMI, the date of the last dose, the evidence of previous response, the length of the gap, the restart dose and the rationale for re-initiation",
    "Reviewed the cautions and recorded a management plan for any that apply, as for a new patient",
    "Explained that the 30 days of contraceptive cover restarts after a restart or a step back up in dose",
  ],
  supplyTitle: "Re-initiation supply complete — Foundayo® tablets",
  supplyReason:
    "Re-initiation criteria met and recorded, restart dose set from the length of the gap, no exclusion criteria triggered, and supply attestation confirmed.",
  extraSafetyNetting: [
    "Re-titrate one step at a time, allowing at least 30 days at each dose, according to tolerability.",
  ],
});

export const ahtWtFoundayoReinitiationPathway: Pathway = {
  id: "aht-wt-foundayo-reinitiation",
  name: "Orforglipron (Foundayo®) — Re-initiation",
  population: "Adults aged 18–84 returning to restart orforglipron tablets within 12 months of the last dose",
  pgdRefs: ["pgd-aht-wt-foundayo"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q_ORIGINAL_BMI,

  nodes: [
    {
      id: Q_ORIGINAL_BMI,
      type: "numeric_input",
      text: "Original (initial) BMI when orforglipron was first started",
      unit: "kg/m²",
      decimals: 1,
      hardMin: 10,
      hardMax: 100,
      storesTo: "fdyReinitOriginalBmi",
      next: Q_EVIDENCE_TEXT,
    },

    {
      id: Q_EVIDENCE_TEXT,
      type: "free_text",
      text: "Evidence of previous response (at least 5% weight loss) and of weight regain since stopping",
      hint: "Record the source, for example the previous provider record, and the weights or dates relied on.",
      required: true,
      minLength: 3,
      storesTo: "fdyReinitEvidenceOfResponse",
      next: Q_RATIONALE,
    },

    {
      id: Q_RATIONALE,
      type: "free_text",
      text: "Rationale for re-initiation",
      hint: "Re-initiation is a considered clinical decision and is not automatic.",
      required: true,
      minLength: 3,
      storesTo: "fdyReinitRationale",
      next: Q_LAST_DOSE_DATE,
    },

    {
      id: Q_LAST_DOSE_DATE,
      type: "date_input",
      text: "Date of the last dose of orforglipron",
      storesTo: "fdyReinitLastDoseDate",
      max: 0,
      next: R_GAP,
    },

    {
      id: R_GAP,
      type: "condition_route",
      conditions: [
        {
          // Fail safe: a missing, invalid or future date gives NaN, which fails both comparisons and is blocked here.
          evaluate: (ctx) => !(daysSinceIsoDate(ctx.context["fdyReinitLastDoseDate"]) <= FDY_REINIT_MAX_DAYS),
          then: { outcomeId: OUT_OVER_12M },
        },
      ],
      default: Q_BELOW_ENTRY,
    },

    {
      id: Q_BELOW_ENTRY,
      type: "yes_no",
      text: "Is the patient's current BMI below the Foundayo entry threshold (below 30; or below 27 with a weight-related comorbidity; or below 27.5 in the listed ethnic backgrounds)?",
      hint: "Re-initiation is permitted below the entry BMI, provided the current BMI is not below 22.5 kg/m². That use is outside the licensed indication and needs recorded consent.",
      answerPolarity: "neutral",
      yes: Q_OFF_LABEL,
      no: Q_CAP,
      auditFlags: { yes: "fdy_reinit_below_entry_bmi_off_label" },
    },

    {
      id: Q_OFF_LABEL,
      type: "multi_select",
      text: "Supply outside the marketing authorisation. Confirm all three.",
      hint: "All three must be confirmed. If any cannot be, supply cannot proceed.",
      items: [
        { id: OFF_LABEL_ITEMS[0], text: "I have explained that the medicine is being supplied outside its licensed indication" },
        { id: OFF_LABEL_ITEMS[1], text: "I have explained the reason, and recorded it" },
        { id: OFF_LABEL_ITEMS[2], text: "The patient has given informed consent to off label supply, and it is recorded" },
      ],
      noneOption: { label: "One or more cannot be confirmed" },
      storesTo: "fdyReinitOffLabelConsent",
      routing: (selections) =>
        OFF_LABEL_ITEMS.every((id) => selections.includes(id)) ? Q_CAP : { outcomeId: OUT_OFF_LABEL_NOT_RECORDED },
    },

    {
      id: Q_CAP,
      type: "yes_no",
      text: "Is the patient taking a strong CYP3A4 inhibitor (ketoconazole, clarithromycin, itraconazole) or a clinical OATP1B inhibitor (ciclosporin)?",
      hint: "The maximum dose with either is 9 mg once daily. This is asked afresh, not carried over.",
      answerPolarity: "neutral",
      storesTo: "fdyReinitCapped",
      yes: R_RESTART_DOSE,
      no: R_RESTART_DOSE,
    },

    {
      id: R_RESTART_DOSE,
      type: "condition_route",
      conditions: [
        {
          // Over 30 days: restart at 0.8 mg, a fixed step. Never a calculated dose.
          evaluate: (ctx) => daysSinceIsoDate(ctx.context["fdyReinitLastDoseDate"]) > FDY_RESUME_MAX_GAP_DAYS,
          then: "fdy-reinit-restart-flag",
        },
        {
          evaluate: (ctx) => ctx.context["fdyReinitCapped"] === true,
          then: Q_RESUME_DOSE_CAPPED,
        },
      ],
      default: Q_RESUME_DOSE,
    },

    {
      id: "fdy-reinit-restart-flag",
      type: "flag",
      variant: "info",
      message:
        "Gap of more than 30 days since the last dose: restart at 0.8 mg once daily and re-titrate in single steps, allowing at least 30 days at each dose, according to tolerability. Record the length of the gap and the restart dose.",
      next: "fdy-reinit-restart-select",
    },

    {
      id: "fdy-reinit-restart-select",
      type: "single_select",
      text: "Restart dose",
      options: [{ id: "fdy-0_8mg", label: "0.8 mg tablets (restart after a gap of more than 30 days)" }],
      routing: { "fdy-0_8mg": downstream.entryNodeId },
      storesTo: "selectedDoseStep",
      required: true,
    },

    {
      id: Q_RESUME_DOSE,
      type: "single_select",
      text: "Gap of 30 days or less: resume the last tolerated dose. Which dose was the last tolerated dose?",
      hint: "Supply the single tablet strength shown. Never use two lower strength tablets.",
      options: STEP_OPTIONS,
      routing: Object.fromEntries(STEP_OPTIONS.map((o) => [o.id, downstream.entryNodeId])),
      storesTo: "selectedDoseStep",
      required: true,
    },

    {
      id: Q_RESUME_DOSE_CAPPED,
      type: "single_select",
      text: "Gap of 30 days or less: resume the last tolerated dose, up to the 9 mg maximum. Which dose was the last tolerated dose?",
      hint: "With a strong CYP3A4 inhibitor or ciclosporin the maximum is 9 mg once daily. A dose above 9 mg is not offered.",
      options: STEP_OPTIONS.filter((o) => ["fdy-0_8mg", "fdy-2_5mg", "fdy-5_5mg", "fdy-9mg"].includes(o.id)),
      routing: Object.fromEntries(
        ["fdy-0_8mg", "fdy-2_5mg", "fdy-5_5mg", "fdy-9mg"].map((id) => [id, downstream.entryNodeId]),
      ),
      storesTo: "selectedDoseStep",
      required: true,
    },

    ...downstream.nodes,
  ],

  outcomes: {
    ...downstream.outcomes,
    [OUT_OVER_12M]: foundayoRedirectOutcome(
      OUT_OVER_12M,
      "Re-initiation not available — Foundayo®",
      "The last dose was more than 12 months ago, or the date could not be confirmed. The patient must satisfy the indication and inclusion criteria afresh and be treated as a new initiation.",
      [
        "Start a New patient (initial supply) consultation.",
        "A new initiation starts at 0.8 mg once daily.",
      ],
    ),
    [OUT_OFF_LABEL_NOT_RECORDED]: foundayoRedirectOutcome(
      OUT_OFF_LABEL_NOT_RECORDED,
      "Cannot supply — off label consent not recorded",
      "Re-initiation below the entry BMI is outside the licensed indication. It needs the explanation, the reason and the patient's informed consent to be recorded.",
      [
        "Explain the off label use and the reason, and obtain consent, before any supply.",
        "Document the discussion.",
        "Refer to the GP if the patient declines.",
      ],
    ),
  },
};
